import { FIELD } from "./fieldKinds.js";
import { formatDateRange, formatLocation } from "../lib/format.js";
import { COMMON_EVENT_REGISTRATION_FIELDS } from "../features/registration/formConfig.js";
import {
  compose, coverSection, aboutSection, gallerySection, chipsSection,
  speakersSection, agendaSection, rsvpSection, subscribeSection, closingSection,
} from "../features/brochure/sections.js";

// Hands-on workshop / training event type.
export default {
  id: "workshop",
  category: "professional",
  label: "Workshop / Training",
  blurb: "Hands-on sessions — modules, facilitators, agenda.",
  icon: "🛠️",
  wizardSteps: [
    {
      id: "basics",
      title: "The essentials",
      fields: [
        { key: "title", label: "Workshop title", kind: FIELD.TEXT, required: true, placeholder: "Intro to Kubernetes" },
        { key: "subtitle", label: "Tagline", kind: FIELD.TEXT, placeholder: "From zero to deploy in a day" },
        { key: "heroImage", label: "Hero image", kind: FIELD.HERO_IMAGE, previewPage: "cover" },
      ],
    },
    {
      id: "when-where",
      title: "When & where",
      fields: [
        { key: "startDate", label: "Start date", kind: FIELD.DATE, required: true },
        { key: "endDate", label: "End date", kind: FIELD.DATE },
        { key: "venue", label: "Venue", kind: FIELD.TEXT },
        { key: "city", label: "City", kind: FIELD.TEXT },
        { key: "country", label: "Country", kind: FIELD.TEXT },
        { key: "mapsUrl", label: "Google Maps link", kind: FIELD.URL, placeholder: "https://maps.google.com/...", previewPage: "cover" },
      ],
    },
    {
      id: "details",
      title: "Details, contact & registration",
      fields: [
        { key: "description", label: "About the workshop", kind: FIELD.TEXTAREA, previewPage: "details" },
        { key: "registrationUrl", label: "Registration / tickets link", kind: FIELD.URL, previewPage: "cover" },
        {
          key: "collectEventRegistrations",
          label: "Collect registrations inside einvite",
          kind: FIELD.TOGGLE,
          previewPage: "eventRegistration",
          description: "Adds an in-app registration form. Entries stay visible only to the creator.",
          defaultPatch: { eventRegistrationFields: COMMON_EVENT_REGISTRATION_FIELDS.map((field) => ({ ...field })) },
        },
        {
          key: "eventRegistrationFields",
          label: "Registration form builder",
          kind: FIELD.REGISTRATION_FIELDS,
          previewPage: "eventRegistration",
          showWhen: (details) => !!details.collectEventRegistrations,
        },
        { key: "contacts", label: "Contact persons", kind: FIELD.CONTACTS, previewPage: "closing" },
        {
          key: "collectPreRegistration",
          label: "Subscribe for updates",
          kind: FIELD.TOGGLE,
          previewPage: "subscribe",
          description: "A simple public form for attendees to get updates by mobile or email.",
        },
      ],
    },
    {
      id: "program",
      title: "Program",
      fields: [
        { key: "topics", label: "Modules", kind: FIELD.CHIPS, placeholder: "Add a module and press Enter", previewPage: "topics" },
        { key: "speakers", label: "Facilitators", kind: FIELD.SPEAKERS, previewPage: "speakers" },
        { key: "schedule", label: "Agenda", kind: FIELD.SCHEDULE, itemLabel: "session", previewPage: "schedule" },
      ],
    },
    {
      id: "custom-slides",
      title: "Custom slides",
      fields: [
        { key: "customSlides", label: "Slides", kind: FIELD.CUSTOM_SLIDES, previewPage: "customSlide" },
      ],
    },
  ],
  planPages(event) {
    const details = event.details || {};
    return compose(
      coverSection(event),
      aboutSection(details),
      gallerySection(details),
      chipsSection(details, { heading: "Modules" }),
      speakersSection(details, { heading: "Facilitators" }),
      agendaSection(details, "schedule", { title: "Agenda" }),
      rsvpSection(event),
      subscribeSection(event),
      closingSection(details),
    );
  },
  deriveOg(details = {}) {
    const when = formatDateRange(details.startDate, details.endDate);
    const where = formatLocation(details);
    return {
      title: details.title || "A workshop",
      description: [details.subtitle, when, where].filter(Boolean).join(" · "),
    };
  },
};
